import React, { useState, useEffect } from 'react';
import './Friends.css';

const API_URL = process.env.REACT_APP_API_URL;

function Friends({ onClose, onStartChallenge }) {
    const [activeTab, setActiveTab] = useState('friends');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [friends, setFriends] = useState([]);
    const [requests, setRequests] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    useEffect(() => {
        loadFriends();
        loadRequests();
        loadChallenges();
    }, []);

    const loadFriends = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('No hay sesión activa');
                return;
            }
            
            const response = await fetch(`${API_URL}/friends/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            setFriends(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error cargando amigos:', error);
            setFriends([]);
            setError('Error al cargar amigos');
        }
    };

    const loadRequests = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const response = await fetch(`${API_URL}/friends/requests`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }
            
            const data = await response.json();
            setRequests(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error cargando solicitudes:', error);
            setRequests([]);
        }
    };

    const loadChallenges = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await fetch(`${API_URL}/challenges/pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setChallenges(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error cargando desafíos:', error);
        }
    };

    const searchUsers = async () => {
        setError(null);
        
        if (searchQuery.length < 3) {
            setError('Ingresa al menos 3 caracteres del email');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No hay sesión activa');
            }
            
            const url = `${API_URL}/friends/search?query=${encodeURIComponent(searchQuery)}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Error ${response.status}`);
            }
            
            const data = await response.json();
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error buscando usuarios:', error);
            setError(error.message || 'Error al buscar usuarios');
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const sendFriendRequest = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/friends/request`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ amigoId: userId })
            });

            if (response.ok) {
                showNotification('✅ Solicitud enviada correctamente', 'success');
                searchUsers();
            } else {
                const error = await response.json();
                showNotification(error.error || 'Error al enviar solicitud', 'error');
            }
        } catch (error) {
            console.error('Error enviando solicitud:', error);
            showNotification('Error al enviar solicitud', 'error');
        }
    };

    const acceptRequest = async (requestId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/friends/accept/${requestId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                loadRequests();
                loadFriends();
            }
        } catch (error) {
            console.error('Error aceptando solicitud:', error);
        }
    };

    const rejectRequest = async (requestId) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/friends/reject/${requestId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            loadRequests();
        } catch (error) {
            console.error('Error rechazando solicitud:', error);
        }
    };

    const removeFriend = async (friendId) => {
        setNotification({
            message: '¿Eliminar este amigo?',
            type: 'confirm',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await fetch(`${API_URL}/friends/${friendId}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    showNotification('✅ Amigo eliminado', 'success');
                    loadFriends();
                } catch (error) {
                    console.error('Error eliminando amigo:', error);
                    showNotification('Error al eliminar amigo', 'error');
                }
            }
        });
    };

    const sendChallenge = async (friendId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/challenges`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ retadoId: friendId })
            });
            const data = await response.json();
            if (response.ok) {
                showNotification(`⚔️ Desafío enviado! Oponente: ${data.oponente}`, 'success');
                loadChallenges();
                setActiveTab('challenges');
            } else {
                showNotification(data.error || 'Error al enviar desafío', 'error');
            }
        } catch (error) {
            showNotification('Error al enviar desafío', 'error');
        }
    };

    const acceptChallenge = async (challengeId, oponenteIa) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/challenges/${challengeId}/accept`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                showNotification('⚔️ Desafío aceptado! A pelear!', 'success');
                loadChallenges();
                if (onStartChallenge) {
                    setTimeout(() => onStartChallenge(challengeId, oponenteIa), 1000);
                }
            }
        } catch (error) {
            showNotification('Error al aceptar desafío', 'error');
        }
    };

    const rejectChallenge = async (challengeId) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/challenges/${challengeId}/reject`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            showNotification('Desafío rechazado', 'success');
            loadChallenges();
        } catch (error) {
            showNotification('Error al rechazar desafío', 'error');
        }
    };

    const getOpponentName = (id) => {
        const names = { novice: 'NOVICIO', boosabon: 'BOOSABON', sabon: 'SABON', 'sabon-nim': 'SABON NIM', champion: 'CAMPEÓN', master: 'MASTER', grandmaster: 'GRAN MASTER' };
        return names[id] || id;
    };

    const getStatusBadge = (estado) => {
        if (estado === 'aceptado') return <span className="status-badge accepted">Amigos</span>;
        if (estado === 'pendiente') return <span className="status-badge pending">Pendiente</span>;
        return null;
    };

    return (
        <div className="friends-modal">
            {notification && (
                <div className="notification-overlay" style={{ zIndex: 10001 }}>
                    <div className={`notification-modal ${notification.type}`}>
                        {notification.type === 'confirm' ? (
                            <>
                                <div className="notification-icon">⚠️</div>
                                <p>{notification.message}</p>
                                <div className="notification-buttons">
                                    <button onClick={() => setNotification(null)} className="btn-secondary">Cancelar</button>
                                    <button onClick={() => { notification.onConfirm(); setNotification(null); }} className="btn-primary">Confirmar</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="notification-icon">{notification.type === 'success' ? '✅' : '❌'}</div>
                                <p>{notification.message}</p>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className="friends-container">
                <div className="friends-header">
                    <h2>🥋 Amigos</h2>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="friends-tabs">
                    <button 
                        className={activeTab === 'friends' ? 'active' : ''}
                        onClick={() => setActiveTab('friends')}
                    >
                        Mis Amigos ({friends.length})
                    </button>
                    <button 
                        className={activeTab === 'search' ? 'active' : ''}
                        onClick={() => setActiveTab('search')}
                    >
                        Buscar
                    </button>
                    <button 
                        className={activeTab === 'requests' ? 'active' : ''}
                        onClick={() => setActiveTab('requests')}
                    >
                        Solicitudes ({requests.length})
                    </button>
                    <button 
                        className={activeTab === 'challenges' ? 'active' : ''}
                        onClick={() => setActiveTab('challenges')}
                    >
                        ⚔️ Desafíos ({challenges.length})
                    </button>
                </div>

                <div className="friends-content">
                    {error && (
                        <div className="error-message" style={{
                            padding: '15px',
                            background: 'rgba(244, 67, 54, 0.2)',
                            border: '2px solid #f44336',
                            borderRadius: '10px',
                            color: '#f44336',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            ⚠️ {error}
                        </div>
                    )}
                    
                    {activeTab === 'search' && (
                        <div className="search-tab">
                            <div className="search-box">
                                <input
                                    type="email"
                                    placeholder="Buscar por email..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                                />
                                <button className="btn-search" onClick={searchUsers} disabled={loading}>
                                    {loading ? '🔍...' : '🔍 Buscar'}
                                </button>
                            </div>
                            <div className="users-list">
                                {searchResults.map(user => (
                                    <div key={user.id} className="user-card">
                                        <div className="user-info">
                                            {user.avatar && (
                                                <img src={user.avatar} alt={user.nombre} className="user-avatar" />
                                            )}
                                            <div>
                                                <div className="user-name">{user.nombre}</div>
                                                <div className="user-details">
                                                    Nivel {user.nivel} - {user.cinturon}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="user-actions">
                                            {getStatusBadge(user.amistad_estado)}
                                            {!user.amistad_estado && (
                                                <button 
                                                    className="btn-add"
                                                    onClick={() => sendFriendRequest(user.id)}
                                                >
                                                    Agregar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {searchResults.length === 0 && !loading && searchQuery && (
                                    <div className="no-results">No se encontraron usuarios con ese email</div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'requests' && (
                        <div className="requests-tab">
                            {requests.length === 0 ? (
                                <div className="no-results">No tienes solicitudes pendientes</div>
                            ) : (
                                <div className="users-list">
                                    {requests.map(request => (
                                        <div key={request.id} className="user-card">
                                            <div className="user-info">
                                                {request.avatar && (
                                                    <img src={request.avatar} alt={request.nombre} className="user-avatar" />
                                                )}
                                                <div>
                                                    <div className="user-name">{request.nombre}</div>
                                                    <div className="user-details">
                                                        Nivel {request.nivel} - {request.cinturon}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="user-actions">
                                                <button 
                                                    className="btn-accept"
                                                    onClick={() => acceptRequest(request.id)}
                                                >
                                                    ✓ Aceptar
                                                </button>
                                                <button 
                                                    className="btn-reject"
                                                    onClick={() => rejectRequest(request.id)}
                                                >
                                                    ✕ Rechazar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'challenges' && (
                        <div className="challenges-tab">
                            {challenges.length === 0 ? (
                                <div className="no-results">No tienes desafíos activos. ¡Desafía a un amigo!</div>
                            ) : (
                                <div className="users-list">
                                    {challenges.map(ch => {
                                        const userId = parseInt(localStorage.getItem('userId'));
                                        const esRetador = ch.retador_id === userId;
                                        const oponenteNombre = esRetador ? ch.retado_nombre : ch.retador_nombre;
                                        const oponenteAvatar = esRetador ? ch.retado_avatar : ch.retador_avatar;
                                        const puedeAceptar = !esRetador && ch.estado === 'pendiente';
                                        const puedeJugar = ch.estado === 'aceptado' || 
                                            (esRetador && ch.estado === 'jugando_retado' && ch.score_retador === null) ||
                                            (!esRetador && ch.estado === 'jugando_retador' && ch.score_retado === null);
                                        const esperando = (esRetador && ch.score_retador !== null && ch.score_retado === null) ||
                                            (!esRetador && ch.score_retado !== null && ch.score_retador === null);
                                        const completado = ch.estado === 'completado';
                                        const gane = completado && ch.ganador_id === userId;
                                        const empate = completado && ch.ganador_id === null;

                                        return (
                                            <div key={ch.id} className="user-card">
                                                <div className="user-info">
                                                    {oponenteAvatar && <img src={oponenteAvatar} alt="" className="user-avatar" />}
                                                    <div>
                                                        <div className="user-name">vs {oponenteNombre}</div>
                                                        <div className="user-details">
                                                            🤖 {getOpponentName(ch.oponente_ia)} • 
                                                            {ch.estado === 'pendiente' && (esRetador ? ' Esperando respuesta...' : ' ¡Te desafió!')}
                                                            {puedeJugar && ' ¡Listo para pelear!'}
                                                            {esperando && ' Esperando que juegue...'}
                                                            {completado && gane && ' 🏆 ¡Ganaste!'}
                                                            {completado && !gane && !empate && ' 😢 Perdiste'}
                                                            {completado && empate && ' 🤝 ¡Empate!'}
                                                        </div>
                                                        {completado && (
                                                            <div className="user-details" style={{fontSize: '11px', marginTop: '4px'}}>
                                                                Tú: ❤️{esRetador ? ch.vida_retador : ch.vida_retado} — 
                                                                {oponenteNombre}: ❤️{esRetador ? ch.vida_retado : ch.vida_retador}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="user-actions">
                                                    {puedeAceptar && (
                                                        <>
                                                            <button className="btn-accept" onClick={() => acceptChallenge(ch.id, ch.oponente_ia)}>✓ Aceptar</button>
                                                            <button className="btn-reject" onClick={() => rejectChallenge(ch.id)}>✕</button>
                                                        </>
                                                    )}
                                                    {puedeJugar && (
                                                        <button className="btn-challenge" onClick={() => onStartChallenge && onStartChallenge(ch.id, ch.oponente_ia)}>🥊 Pelear</button>
                                                    )}
                                                    {esperando && <span className="status-badge pending">Esperando...</span>}
                                                    {esRetador && ch.estado === 'pendiente' && <span className="status-badge pending">Enviado</span>}
                                                    {completado && gane && <span className="status-badge accepted">🏆 Victoria</span>}
                                                    {completado && !gane && !empate && <span className="status-badge" style={{background:'rgba(244,67,54,0.3)',color:'#f44336'}}>Derrota</span>}
                                                    {completado && empate && <span className="status-badge pending">Empate</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'friends' && (
                        <div className="friends-tab">
                            {friends.length === 0 ? (
                                <div className="no-results">
                                    Aún no tienes amigos. ¡Busca usuarios y envía solicitudes!
                                </div>
                            ) : (
                                <div className="users-list">
                                    {friends.map(friend => (
                                        <div key={friend.id} className="user-card">
                                            <div className="user-info">
                                                {friend.avatar && (
                                                    <img src={friend.avatar} alt={friend.nombre} className="user-avatar" />
                                                )}
                                                <div>
                                                    <div className="user-name">{friend.nombre}</div>
                                                    <div className="user-details">
                                                        Nivel {friend.nivel} - {friend.cinturon}
                                                    </div>
                                                    {(friend.desafios_ganados > 0 || friend.desafios_perdidos > 0 || friend.desafios_empates > 0) && (
                                                        <div className="user-details" style={{fontSize: '11px', marginTop: '3px'}}>
                                                            ⚔️ {friend.desafios_ganados}W - {friend.desafios_perdidos}L - {friend.desafios_empates}D
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="user-actions">
                                                <button 
                                                    className="btn-challenge"
                                                    onClick={() => sendChallenge(friend.id)}
                                                >
                                                    ⚔️ Desafiar
                                                </button>
                                                <button 
                                                    className="btn-remove"
                                                    onClick={() => removeFriend(friend.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Friends;
